import { useCallback, useEffect, useRef } from "react";
import type SocketInterface from "../../interface/socket_interface";
import { setLocalMicStream } from "../../interface/local_mic_stream_registry";
import { showCockpitToast } from "../../app/common/cockpit_toast";
import { streamHasLiveAudio } from "./remote_audio_registry";

/**
 * useOperatorMicTrack — 콕핏 마이크(운전자 → 차량 스피커) 아웃바운드 트랙 소유/부착.
 *
 * **반드시 "항상 마운트되는" 컴포넌트(AdRemoteClient)에서 호출해야 한다.**
 * WebRTC offer 는 시그널링 이벤트(join-success / peer-joined)로 시작되며,
 * socket_interface.invokeEventAsync("offerConnection") 는 등록된 핸들러가 없으면 즉시
 * 반환한다. 예전에는 이 핸들러를 콕핏 화면(SceneInterpreter → Communicator, lazy 청크)에서
 * 등록했는데, 콕핏은 "세션 연결이 끝난 뒤"에야 마운트되므로 최초 연결에서는 offer 생성
 * 시점에 핸들러가 0개였다. 그 결과 sendonly 오디오 m-line 은 있지만 트랙/SSRC 가 없는 채로
 * 협상되어 차량의 add-track 에 콕핏 마이크가 빠졌다(= 차량 스피커 무음).
 * 앱 시작 시점에 등록하면 어떤 offer 보다 먼저 준비되어 이 경합이 사라진다.
 */

// 마이크 트랙 부착이 권한 지연/거부로 negotiation 을 영구히 막지 않도록 하는 상한.
// (로그인 제스처에서 권한을 사전 획득하므로 보통 즉시 resolve 된다.)
const MIC_ATTACH_TIMEOUT_MS = 1500;

function isPeerClosed(pc: RTCPeerConnection): boolean {
  return (
    String(pc.signalingState || "") === "closed" ||
    String(pc.connectionState || "") === "closed"
  );
}

export function findOutboundAudioTransceiver(
  pc: RTCPeerConnection,
): RTCRtpTransceiver | null {
  if (typeof pc.getTransceivers !== "function") {
    return null;
  }

  const transceivers = pc.getTransceivers();
  // 1순위: 트랙 종류가 audio 로 확정된 송신 송수신기.
  const byKind = transceivers.find((transceiver) => {
    const kind =
      transceiver.receiver?.track?.kind || transceiver.sender?.track?.kind || "";
    return (
      kind === "audio" &&
      (transceiver.direction === "sendonly" ||
        transceiver.direction === "sendrecv")
    );
  });
  if (byKind) {
    return byKind;
  }

  // 폴백: 협상 전에는 트랙이 없어 kind 가 비어 있다(="" → 위 매칭 실패). 이때 마이크가
  // 간헐적으로 안 붙던 회귀가 있었다(addTrack 이 새 m-line 을 만들기도 해 브라우저별로
  // 불안정). 비디오 송수신기는 모두 recvonly 이므로, "video 가 아닌 sendonly/sendrecv"
  // 송수신기를 아웃바운드 오디오로 간주해 안정적으로 replaceTrack 한다.
  return (
    transceivers.find((transceiver) => {
      const kind =
        transceiver.receiver?.track?.kind ||
        transceiver.sender?.track?.kind ||
        "";
      return (
        kind !== "video" &&
        (transceiver.direction === "sendonly" ||
          transceiver.direction === "sendrecv")
      );
    }) || null
  );
}

export default function useOperatorMicTrack(socket: SocketInterface | null) {
  const localMicStreamRef = useRef<MediaStream | null>(null);
  const localMicStreamPromiseRef = useRef<Promise<MediaStream | null> | null>(
    null,
  );
  const onOfferPeerConnectionRef = useRef<
    (pc: RTCPeerConnection) => Promise<void> | void
  >(() => Promise.resolve());

  const releaseLocalMicStream = useCallback(() => {
    const stream = localMicStreamRef.current;
    if (stream) {
      stream.getTracks().forEach((track) => {
        if (typeof track.stop === "function") {
          track.stop();
        }
      });
    }
    localMicStreamRef.current = null;
    setLocalMicStream(null);
  }, []);

  const requestLocalMicStream = useCallback((): Promise<MediaStream | null> => {
    if (localMicStreamRef.current) {
      if (streamHasLiveAudio(localMicStreamRef.current)) {
        return Promise.resolve(localMicStreamRef.current);
      }
      // 캐시된 마이크 트랙이 죽은 경우(기기 분리/교체, OS 회수, 다른 앱의 독점 점유 등).
      // 그대로 replaceTrack 하면 오류 없이 "무음"만 계속 전송되고, 페이지를 새로고침하기
      // 전까지 복구되지 않는다. 버리고 재획득한다.
      releaseLocalMicStream();
    }

    if (localMicStreamPromiseRef.current) {
      return localMicStreamPromiseRef.current;
    }

    if (navigator.mediaDevices?.getUserMedia) {
      localMicStreamPromiseRef.current = navigator.mediaDevices
        .getUserMedia({ video: false, audio: true })
        .then((stream) => {
          localMicStreamRef.current = stream;
          // 표시 전용 공유 seam 에 라이브 마이크 스트림을 등록(오디오 레벨 게이지용).
          setLocalMicStream(stream);
          return stream;
        })
        .catch((error) => {
          console.log(error);
          return null;
        })
        .finally(() => {
          localMicStreamPromiseRef.current = null;
        });
      return localMicStreamPromiseRef.current;
    }

    if (navigator.webkitGetUserMedia) {
      localMicStreamPromiseRef.current = new Promise<MediaStream | null>(
        (resolve) => {
          navigator.webkitGetUserMedia!(
            { video: false, audio: true },
            (stream) => {
              localMicStreamRef.current = stream;
              setLocalMicStream(stream);
              resolve(stream);
            },
            (error) => {
              console.log(error);
              resolve(null);
            },
          );
        },
      ).finally(() => {
        localMicStreamPromiseRef.current = null;
      });
      return localMicStreamPromiseRef.current;
    }

    showCockpitToast(
      "We cannot access your audio. You need to check browser setting!",
      { alert: true },
    );
    return Promise.resolve(null);
  }, [releaseLocalMicStream]);

  // 마이크 트랙을 이 PeerConnection 의 아웃바운드 오디오에 붙인다.
  // offer 에 sendonly 오디오 m-line 이 이미 있으므로, 늦게 호출돼도 replaceTrack 만으로
  // 재협상 없이 송신이 시작된다.
  const attachMicTrackToPeer = useCallback(
    async (pc: RTCPeerConnection, stream: MediaStream): Promise<void> => {
      if (isPeerClosed(pc)) {
        return;
      }

      const tracks =
        typeof stream.getAudioTracks === "function"
          ? stream.getAudioTracks()
          : stream.getTracks().filter((track) => track.kind === "audio");
      if (tracks.length === 0) {
        return;
      }

      const audioTrack = tracks[0];
      const outboundAudioTransceiver = findOutboundAudioTransceiver(pc);
      if (outboundAudioTransceiver?.sender?.replaceTrack) {
        await outboundAudioTransceiver.sender
          .replaceTrack(audioTrack)
          .catch((error) => {
            if (!isPeerClosed(pc)) {
              console.log(error);
            }
          });
        return;
      }

      const senders = typeof pc.getSenders === "function" ? pc.getSenders() : [];
      const audioSender = senders.find(
        (sender) => sender.track?.kind === "audio",
      );

      if (audioSender?.replaceTrack) {
        await audioSender.replaceTrack(audioTrack).catch((error) => {
          if (!isPeerClosed(pc)) {
            console.log(error);
          }
        });
        return;
      }

      if (senders.some((sender) => sender.track === audioTrack)) {
        return;
      }

      tracks.forEach((track) => {
        if (isPeerClosed(pc)) {
          return;
        }
        try {
          pc.addTrack(track, stream);
        } catch (error) {
          if (
            !(error instanceof DOMException) ||
            error.name !== "InvalidStateError"
          ) {
            console.log(error);
          }
        }
      });
    },
    [],
  );

  // 최신 클로저를 ref 에 담아, 핸들러를 재등록하지 않고도 항상 최신 로직이 돌게 한다
  // (socket.registerEvent 는 해제 API 가 없어 재등록하면 핸들러가 누적된다).
  onOfferPeerConnectionRef.current = async (pc: RTCPeerConnection) => {
    // 마이크 트랙은 offer SDP 생성 "전에" 붙어야 m-line 이 트랙/SSRC 를 포함해 협상된다.
    // invokeEventAsync 가 이 핸들러를 await 하므로, 여기서 부착까지 마치고 반환하면
    // sendLocalDescription 이 그 후에 실행된다. 단, 권한 지연/거부가 negotiation 을
    // 영구히 막지 않도록 짧은 타임아웃으로 가드한다.
    const pendingMicStream = requestLocalMicStream();
    const stream = await Promise.race([
      pendingMicStream,
      new Promise<MediaStream | null>((resolve) =>
        setTimeout(() => resolve(null), MIC_ATTACH_TIMEOUT_MS),
      ),
    ]);

    if (stream) {
      await attachMicTrackToPeer(pc, stream);
      return;
    }

    // 타임아웃(권한 지연/기기 초기화 지연)으로 경쟁에서 진 경우. negotiation 은 계속
    // 진행시키되, 스트림이 뒤늦게 준비되면 그때 붙인다.
    void pendingMicStream.then((lateStream) => {
      if (lateStream) {
        void attachMicTrackToPeer(pc, lateStream);
      }
    });
  };

  // 로그인 제스처 등에서 미리 마이크를 확보해 두면(권한 팝업 이미 통과) offer 시점에
  // 즉시 트랙을 붙일 수 있다. 실패해도 offer 경로가 다시 시도하므로 안전하다.
  const prewarmMic = useCallback(() => {
    void requestLocalMicStream();
  }, [requestLocalMicStream]);

  useEffect(() => {
    if (!socket) {
      return undefined;
    }
    const handler = (pc: RTCPeerConnection) =>
      onOfferPeerConnectionRef.current(pc);
    socket.registerEvent("offerConnection", handler, null);
    return () => {
      // 트랙 정지 + 공유 seam 해제(게이지가 더 이상 죽은 스트림을 읽지 않도록).
      releaseLocalMicStream();
      localMicStreamPromiseRef.current = null;
    };
  }, [socket, releaseLocalMicStream]);

  return { prewarmMic };
}
